/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { get } from 'utils/lodash'

type ContainerRecord = Record<string, unknown>

// Which surface opened the cancel dialog, so two mounted copies cannot both answer one click.
export const CANCEL_PLACEMENT_SOURCE = {
  accordion: 'cs-containers-accordion',
  header: 'cs-header-menu',
  mapPanel: 'cs-map-routing-panel',
  inventory: 'inventory-list',
}

type InventoryContainersState = {
  confirmDeleteContainerDialog: { isOpen: boolean; containerIds: string[] }
  copyContainerFieldDialog: { isOpen: boolean; direction: string; containerIds: string[] }
  containerDetailsPopover: { isOpen: boolean; id: string; position: { x: number; y: number } | null; container: ContainerRecord }
  moveContainerDialog: { isOpen: boolean; container: ContainerRecord | null }
  assignContainerDialog: { isOpen: boolean; placement: ContainerRecord | null }
  cancelPlacementDialog: { isOpen: boolean; placement: ContainerRecord | null; source: string }
  // The service-level ending the cancel fork hands off to; `source` keeps one surface answering.
  deactivateServiceDrawer: { isOpen: boolean; source: string; containerId?: string }
  // One container that stays where it is: sold, written off, or the customer's all along.
  leaveOnSiteDialog: { isOpen: boolean; container: ContainerRecord | null }
}

const initialState: InventoryContainersState = {
  confirmDeleteContainerDialog: { isOpen: false, containerIds: [] },
  copyContainerFieldDialog: { isOpen: false, direction: '', containerIds: [] },
  containerDetailsPopover: { isOpen: false, id: '', position: null, container: {} },
  moveContainerDialog: { isOpen: false, container: null },
  assignContainerDialog: { isOpen: false, placement: null },
  cancelPlacementDialog: { isOpen: false, placement: null, source: '' },
  deactivateServiceDrawer: { isOpen: false, source: '', containerId: '' },
  leaveOnSiteDialog: { isOpen: false, container: null },
}

const inventoryContainersSlice = createSlice({
  name: 'inventoryContainers',
  initialState: { ...initialState },
  reducers: {
    setConfirmDeleteContainerDialog: (state, action: PayloadAction<InventoryContainersState['confirmDeleteContainerDialog']>) => {
      state.confirmDeleteContainerDialog = action.payload
    },
    setCopyContainerFieldDialog: (state, action: PayloadAction<InventoryContainersState['copyContainerFieldDialog']>) => ({
      ...state,
      copyContainerFieldDialog: action.payload,
    }),
    setContainerDetailsPopoverState: (state, action: PayloadAction<Partial<InventoryContainersState['containerDetailsPopover']>>) => {
      state.containerDetailsPopover = { ...state.containerDetailsPopover, ...action.payload }
    },
    setMoveContainerDialogState: (state, action: PayloadAction<Partial<InventoryContainersState['moveContainerDialog']>>) => {
      state.moveContainerDialog = { ...state.moveContainerDialog, ...action.payload }
    },
    setAssignContainerDialogState: (state, action: PayloadAction<Partial<InventoryContainersState['assignContainerDialog']>>) => {
      state.assignContainerDialog = { ...state.assignContainerDialog, ...action.payload }
    },
    setDeactivateServiceDrawerState: (state, action: PayloadAction<Partial<InventoryContainersState['deactivateServiceDrawer']>>) => {
      state.deactivateServiceDrawer = { ...state.deactivateServiceDrawer, ...action.payload }
    },
    setCancelPlacementDialogState: (state, action: PayloadAction<Partial<InventoryContainersState['cancelPlacementDialog']>>) => {
      state.cancelPlacementDialog = { ...state.cancelPlacementDialog, ...action.payload }
    },
    setLeaveOnSiteDialogState: (state, action: PayloadAction<Partial<InventoryContainersState['leaveOnSiteDialog']>>) => {
      state.leaveOnSiteDialog = { ...state.leaveOnSiteDialog, ...action.payload }
    },
    resetState: () => {
      return { ...initialState }
    },
  },
})

export const {
  setConfirmDeleteContainerDialog,
  setCopyContainerFieldDialog,
  setContainerDetailsPopoverState,
  setMoveContainerDialogState,
  setAssignContainerDialogState,
  setCancelPlacementDialogState,
  setDeactivateServiceDrawerState,
  setLeaveOnSiteDialogState,
  resetState,
} = inventoryContainersSlice.actions

export const selectConfirmDeleteContainerDialog = (s: unknown) => get(s, 'inventoryContainers.confirmDeleteContainerDialog')
export const selectCopyContainerFieldDialog = (s: unknown) => get(s, 'inventoryContainers.copyContainerFieldDialog')
export const selectContainerDetailsPopover = (s: unknown) => get(s, 'inventoryContainers.containerDetailsPopover')
export const selectMoveContainerDialog = (s: unknown) => get(s, 'inventoryContainers.moveContainerDialog')
export const selectAssignContainerDialog = (s: unknown) => get(s, 'inventoryContainers.assignContainerDialog')
export const selectCancelPlacementDialog = (s: unknown) => get(s, 'inventoryContainers.cancelPlacementDialog')
export const selectDeactivateServiceDrawer = (s: unknown) => get(s, 'inventoryContainers.deactivateServiceDrawer')
export const selectLeaveOnSiteDialog = (s: unknown) => get(s, 'inventoryContainers.leaveOnSiteDialog')

export default inventoryContainersSlice.reducer
